import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth';
import { isNative } from './env';
import { storageGet, storageSet, storageRemove } from './storage';
import { secureGet, secureSet, secureRemove } from './secureStore';

// Pattern adapted from Closing Master:
//   1. After first app launch, surface a one-time opt-in (Enable / Not Now).
//      Track in `biometric:asked` so we never ask twice.
//   2. If the user opts in, save email+password into device storage on the
//      next successful login. Email lives in Preferences; password lives in
//      Keychain via secure-storage.
//   3. On subsequent login screens, auto-prompt biometry; on success replay
//      the saved credentials through the normal login dispatch.
//   4. A wrapper component (BiometricLockScreen) re-prompts after 5 minutes
//      of background time, or on cold start with an active session.

const ENABLED_KEY = 'biometric:enabled';        // 'true' | absent
const ASKED_KEY = 'biometric:asked';            // 'true' | absent
const CRED_EMAIL_KEY = 'biometric:email';       // Preferences (non-sensitive)
const CRED_PASSWORD_KEY = 'biometric:password'; // secure-storage (Keychain)

export async function isBiometricAvailable() {
    if (!isNative) return false;
    try {
        const r = await BiometricAuth.checkBiometry();
        return !!r.isAvailable && r.biometryType !== BiometryType.none;
    } catch {
        return false;
    }
}

export async function getBiometricLabel() {
    if (!isNative) return 'Biometric';
    try {
        const r = await BiometricAuth.checkBiometry();
        switch (r.biometryType) {
            case BiometryType.faceId: return 'Face ID';
            case BiometryType.touchId: return 'Touch ID';
            case BiometryType.fingerprintAuthentication: return 'Fingerprint';
            case BiometryType.faceAuthentication: return 'Face Unlock';
            case BiometryType.irisAuthentication: return 'Iris';
            default: return 'Biometric';
        }
    } catch {
        return 'Biometric';
    }
}

// Opt-in flag (user said "yes use biometrics for login").
export async function getBiometricOptIn() {
    if (!isNative) return false;
    return (await storageGet(ENABLED_KEY)) === 'true';
}
export async function setBiometricOptIn(value) {
    if (!isNative) return;
    if (value) {
        await storageSet(ENABLED_KEY, 'true');
    } else {
        await storageRemove(ENABLED_KEY);
        await clearCredentials();
    }
}

// One-time opt-in prompt tracking.
export async function hasBiometricOptInBeenAsked() {
    if (!isNative) return true; // skip on web
    return (await storageGet(ASKED_KEY)) === 'true';
}
export async function markBiometricOptInAsked() {
    if (!isNative) return;
    await storageSet(ASKED_KEY, 'true');
}

// Credential vault. Email is fine in Preferences; password lives encrypted
// in Keychain (iOS) / EncryptedSharedPreferences (Android).
export async function saveCredentials(email, password) {
    if (!isNative) return;
    if (!email || !password) return;
    await storageSet(CRED_EMAIL_KEY, email);
    await secureSet(CRED_PASSWORD_KEY, password);
}
export async function getCredentials() {
    if (!isNative) return null;
    const email = await storageGet(CRED_EMAIL_KEY);
    const password = await secureGet(CRED_PASSWORD_KEY);
    if (email && password) return { email, password };
    return null;
}
export async function hasStoredCredentials() {
    return (await getCredentials()) !== null;
}
export async function clearCredentials() {
    if (!isNative) return;
    await storageRemove(CRED_EMAIL_KEY);
    await secureRemove(CRED_PASSWORD_KEY);
}

// Just runs the biometric prompt. Caller decides what to do on success.
export async function authenticateWithBiometrics(reason = 'Sign in to Advice EV') {
    if (!isNative) return false;
    try {
        await BiometricAuth.authenticate({ reason, cancelTitle: 'Cancel' });
        return true;
    } catch {
        return false;
    }
}
